/**
 * Compact in-deal version selector — lives next to the deal header.
 * `touch` = mobile bar (larger hit targets, full-width menu).
 * `compact` = desktop KPI strip.
 *
 * Menu is a body portal (not a Radix dropdown). The KPI header is 30px
 * and z-50; Radix available-height clips the list under that bar.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown, Check, CopySimple, PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import { Input } from './ui/input';
import { preferredScenario } from '../utils/dealScenarios';
import { versionMenuPosition } from '../utils/versionMenuPosition';

function VersionModal({ open, title, description, children, actions, testId }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[120] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-3"
      data-testid={testId}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scenario-modal-title"
    >
      <button
        type="button"
        className="absolute inset-0"
        aria-label="Close"
        onClick={actions.onDismiss}
      />
      <div className="relative w-full sm:max-w-sm bg-white rounded-t-xl sm:rounded-lg border border-gray-200 shadow-xl p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <h2 id="scenario-modal-title" className="text-base sm:text-sm font-semibold text-gray-900">{title}</h2>
        {description ? (
          <p className="text-[13px] sm:text-[9px] text-gray-500 mt-1 leading-snug">{description}</p>
        ) : null}
        {children}
        <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
          {actions.secondary}
          {actions.primary}
        </div>
      </div>
    </div>
  );
}

function VersionNameDialog({ open, title, description, confirmLabel, initialName, placeholder, onClose, onConfirm, testId }) {
  const [name, setName] = useState(initialName || '');

  useEffect(() => {
    if (open) setName(initialName || '');
  }, [open, initialName]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
  };

  return (
    <VersionModal
      open={open}
      title={title}
      description={description}
      testId={testId}
      actions={{
        onDismiss: onClose,
        secondary: (
          <button
            type="button"
            onClick={onClose}
            className="flex-1 min-h-11 sm:min-h-0 sm:flex-none text-sm sm:text-[10px] text-gray-600 hover:text-gray-800 px-3 py-2 sm:py-1 rounded-md hover:bg-gray-100"
          >
            Cancel
          </button>
        ),
        primary: (
          <button
            type="button"
            onClick={submit}
            disabled={!name.trim()}
            className="flex-1 min-h-11 sm:min-h-0 sm:flex-none text-sm sm:text-[10px] text-white bg-gray-900 hover:bg-gray-800 px-3 py-2 sm:py-1 rounded-md font-medium disabled:opacity-50"
            data-testid="scenario-name-confirm"
          >
            {confirmLabel}
          </button>
        ),
      }}
    >
      <div className="mt-3 space-y-2">
        <label className="text-[13px] sm:text-[10px] text-gray-500 font-medium" htmlFor="scenario-name-input">Name</label>
        <Input
          id="scenario-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder={placeholder}
          className="h-11 sm:h-7 text-base sm:text-[11px]"
          autoFocus
          data-testid="scenario-name-input"
        />
      </div>
    </VersionModal>
  );
}

function DeleteVersionDialog({ open, name, onClose, onConfirm }) {
  return (
    <VersionModal
      open={open}
      title="Delete version?"
      description={`“${name}” will be removed from this deal. At least one version always remains.`}
      testId="scenario-delete-dialog"
      actions={{
        onDismiss: onClose,
        secondary: (
          <button
            type="button"
            onClick={onClose}
            className="flex-1 min-h-11 sm:min-h-0 sm:flex-none text-sm sm:text-[10px] text-gray-600 hover:text-gray-800 px-3 py-2 sm:py-1 rounded-md hover:bg-gray-100"
          >
            Cancel
          </button>
        ),
        primary: (
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 min-h-11 sm:min-h-0 sm:flex-none text-sm sm:text-[10px] text-white bg-red-600 hover:bg-red-700 px-3 py-2 sm:py-1 rounded-md font-medium"
            data-testid="scenario-delete-confirm"
          >
            Delete
          </button>
        ),
      }}
    />
  );
}

export function ScenarioVersionControls({
  scenarios = [],
  activeScenarioId,
  onSelect,
  onCreate,
  onRename,
  onDuplicate,
  onDelete,
  size = 'compact',
}) {
  const ordered = [...scenarios].sort((a, b) => (Number(a.ordinal) || 0) - (Number(b.ordinal) || 0));
  const active = scenarios.find((s) => s.id === activeScenarioId) || preferredScenario(scenarios);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [nameDialog, setNameDialog] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const triggerRef = useRef(null);
  const canDelete = scenarios.length > 1;
  const touch = size === 'touch';

  const closeMenu = () => setMenuOpen(false);
  const openCreate = () => {
    closeMenu();
    setNameDialog({ mode: 'create' });
  };
  const openRename = (scenario) => {
    closeMenu();
    setNameDialog({ mode: 'rename', scenario });
  };

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuStyle(null);
      return undefined;
    }
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      const width = touch
        ? Math.min(352, (typeof window !== 'undefined' ? window.innerWidth : 390) - 16)
        : 256;
      setMenuStyle(versionMenuPosition(rect, { width, maxHeight: touch ? 384 : 360 }));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [menuOpen, touch, scenarios.length]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  if (!active) return null;

  return (
    <div
      className={`flex items-center min-w-0 ${touch ? 'gap-1 w-full' : 'gap-0.5'}`}
      data-testid="scenario-version-controls"
      data-size={size}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`flex items-center gap-1 min-w-0 rounded text-left hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
          touch
            ? 'h-9 flex-1 px-2 max-w-full'
            : 'h-5 max-w-[148px] px-1 py-0.5'
        }`}
        title={`Version: ${active.name}`}
        aria-label={`Active version ${active.name}`}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        data-testid="scenario-version-selector"
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span
          className={`text-gray-400 tabular-nums shrink-0 ${touch ? 'text-[11px]' : 'text-[7px]'}`}
          data-testid="scenario-version-ordinal"
        >
          {active.ordinal}
        </span>
        <span
          className={`font-medium text-gray-700 truncate ${touch ? 'text-[13px]' : 'text-[9px]'}`}
          data-testid="scenario-version-name"
        >
          {active.name}
        </span>
        <CaretDown weight="bold" className={`text-gray-400 shrink-0 ${touch ? 'w-3.5 h-3.5' : 'w-2.5 h-2.5'}`} />
      </button>

      {menuOpen && menuStyle && typeof document !== 'undefined' ? createPortal(
        <>
          <button
            type="button"
            className="fixed inset-0 z-[199] bg-transparent cursor-default"
            aria-label="Close version menu"
            onClick={closeMenu}
          />
          <div
            role="menu"
            style={menuStyle}
            className="scenario-version-menu z-[200] overflow-y-auto overflow-x-hidden rounded-lg border border-gray-200 !bg-white p-1.5 shadow-xl"
            data-testid="scenario-version-menu"
          >
            {ordered.map((scenario) => {
              const isActive = scenario.id === active.id;
              return (
                <div
                  key={scenario.id}
                  className={`flex items-center gap-1 rounded-md hover:bg-gray-50 ${
                    touch ? 'min-h-11 px-1 py-0.5' : 'px-1.5 py-1.5'
                  }`}
                  data-testid={`scenario-version-item-${scenario.id}`}
                >
                  <button
                    type="button"
                    role="menuitem"
                    className={`flex items-center gap-1.5 min-w-0 flex-1 text-left ${touch ? 'min-h-11' : ''}`}
                    onClick={() => {
                      onSelect(scenario.id);
                      closeMenu();
                    }}
                  >
                    <Check weight="bold" className={`shrink-0 ${touch ? 'w-4 h-4' : 'w-3.5 h-3.5'} ${isActive ? 'text-indigo-600' : 'text-transparent'}`} />
                    <span className={`text-gray-400 tabular-nums shrink-0 ${touch ? 'text-[11px] w-4' : 'text-[11px] w-4'}`}>{scenario.ordinal}</span>
                    <span className={`flex-1 truncate ${touch ? 'text-[14px]' : 'text-[13px]'} ${isActive ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                      {scenario.name}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 ${touch ? 'inline-flex items-center justify-center min-w-11 min-h-11' : 'p-1'}`}
                    title="Rename version"
                    aria-label={`Rename ${scenario.name}`}
                    data-testid={`scenario-rename-btn-${scenario.id}`}
                    onClick={() => openRename(scenario)}
                  >
                    <PencilSimple weight="bold" className={touch ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
                  </button>
                  <button
                    type="button"
                    className={`rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 ${touch ? 'inline-flex items-center justify-center min-w-11 min-h-11' : 'p-1'}`}
                    title="Duplicate version"
                    aria-label={`Duplicate ${scenario.name}`}
                    data-testid={`scenario-duplicate-btn-${scenario.id}`}
                    onClick={() => {
                      onDuplicate(scenario.id);
                      closeMenu();
                    }}
                  >
                    <CopySimple weight="bold" className={touch ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
                  </button>
                  {canDelete && (
                    <button
                      type="button"
                      className={`rounded text-gray-400 hover:text-red-600 hover:bg-red-50 ${touch ? 'inline-flex items-center justify-center min-w-11 min-h-11' : 'p-1'}`}
                      title="Delete version"
                      aria-label={`Delete ${scenario.name}`}
                      data-testid={`scenario-delete-btn-${scenario.id}`}
                      onClick={() => {
                        setDeleteTarget(scenario);
                        closeMenu();
                      }}
                    >
                      <Trash weight="bold" className={touch ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
                    </button>
                  )}
                </div>
              );
            })}
            <div className="my-1 h-px bg-gray-100" />
            <button
              type="button"
              role="menuitem"
              className={`flex w-full items-center gap-1.5 rounded-md text-gray-700 hover:bg-gray-50 ${
                touch ? 'min-h-11 px-2 text-[14px]' : 'px-2 py-2 text-[13px]'
              }`}
              onClick={openCreate}
              data-testid="scenario-menu-add"
            >
              <Plus weight="bold" className={touch ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
              New version from current
            </button>
          </div>
        </>,
        document.body,
      ) : null}

      <button
        type="button"
        onClick={openCreate}
        className={`flex items-center shrink-0 rounded text-indigo-600 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
          touch ? 'h-9 gap-1 px-2' : 'gap-0.5 px-1 py-0.5'
        }`}
        title="Duplicate current version"
        aria-label="Add version"
        data-testid="scenario-add-version-btn"
      >
        <Plus weight="bold" className={touch ? 'w-4 h-4' : 'w-3 h-3'} />
        <span className={`font-semibold ${touch ? 'text-[12px]' : 'text-[8px] hidden sm:inline'}`}>Version</span>
      </button>

      <VersionNameDialog
        open={nameDialog?.mode === 'create'}
        title="New version"
        description="Duplicates this version’s assumptions and calculations. Name it something like “Lower construction cost”."
        confirmLabel="Create"
        initialName=""
        placeholder="Lower construction cost"
        testId="scenario-name-dialog"
        onClose={() => setNameDialog(null)}
        onConfirm={(name) => {
          onCreate(name);
          setNameDialog(null);
        }}
      />
      <VersionNameDialog
        open={nameDialog?.mode === 'rename'}
        title="Rename version"
        description="Custom names stay with this deal — not a fixed v1 / v2 scheme."
        confirmLabel="Rename"
        initialName={nameDialog?.scenario?.name || ''}
        placeholder="Land at CHF 3.7m"
        testId="scenario-rename-dialog"
        onClose={() => setNameDialog(null)}
        onConfirm={(name) => {
          if (nameDialog?.scenario) onRename(nameDialog.scenario.id, name);
          setNameDialog(null);
        }}
      />
      <DeleteVersionDialog
        open={!!deleteTarget}
        name={deleteTarget?.name || ''}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) onDelete(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}
